<script setup lang="ts">
import { PRODUCT_IMAGE_PLACEHOLDER_PATH } from '@/shared/constants/placeholders';
import type { CategoryCard } from '@/pages/catalog/model/types/categoryCard';

const props = defineProps<CategoryCard>();
const localePath = useLocalePath();
const { locale } = useI18n();

const specialLinks: Record<string, string> = {
  promotions: localePath('promotions'),
  new: localePath('new'),
  sale: localePath('sale'),
  popular: localePath('popular'),
  liquidation: localePath('liquidation'),
};

const parentCategoryLink = computed(() => {
  const categorySlug = props.category?.[`slug_${locale.value}`];
  const isSpecialCategory = Object.keys(specialLinks).includes(
    String(props.category?.slug_en),
  );

  const path = isSpecialCategory
    ? specialLinks[props?.category?.slug_en || '']
    : localePath({
        name: 'catalog-category',
        params: { category: categorySlug },
      });

  return path;
});

const cardImage = computed(() => props?.category?.icon || PRODUCT_IMAGE_PLACEHOLDER_PATH);
</script>

<template>
  <nuxt-link
    class="category-card"
    :to="parentCategoryLink"
    :style="{ backgroundImage: `url(${cardImage})` }"
    :aria-label="category?.name"
  >
    <div class="category-card__title">{{ category?.name }}</div>
  </nuxt-link>
</template>

<style lang="less" scoped>
.category-card {
  display: block;
  width: 100%;
  height: 190px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: @color-quarter-spanish-white;
  border-radius: @size-x4;
  position: relative;

  &__title {
    font-size: @size-x5;
    font-weight: 600;
    line-height: 100%;
    position: absolute;
    top: @size-x6;
    left: @size-x5;
    right: @size-x5;
  }

  @media @1024 {
    height: 92px;
    border-radius: @size-x3;

    &__title {
      font-size: 14px;
    }
  }
}
</style>
